/**
 * Pure availability engine. Everything is derived from data passed in; nothing here reads
 * storage or a clock. `now` is epoch ms.
 *
 * Model: a staff member is free for a service starting at `start` when
 *  - `start` is on the slot grid of one of that day's working ranges (step from range start)
 *    and start + duration fits inside the range (breaks are simply gaps between ranges),
 *  - start >= now + leadTime and the day <= today + maxAdvanceDays,
 *  - [start, end) does not touch time off for that staff (or the whole business),
 *  - [start, end + buffer) does not overlap any blocking appointment's [start, blockedUntil).
 */
import {
  type Appointment, type DayKey, type Id, type IsoUtc, type Service, type Settings, type Slot,
  type Staff, type TimeOff, type WeeklyHours, type AppointmentSource,
} from './types';
import {
  MINUTE, addDaysToKey, atMinuteOfDay, dayKeyOf, eachDay, parseHHMM, toIso, toMs, weekdayOfDay,
} from './time';

export interface AvailabilityData {
  tz: string;
  settings: Settings;
  /** All staff of the business; eligibility is derived from the service. */
  staff: Staff[];
  weeklyHours: WeeklyHours[];
  appointments: Appointment[];
  timeOff: TimeOff[];
}
export interface SlotQuery extends AvailabilityData {
  service: Service;
  staffChoice: Id | 'any';
  fromDay: DayKey;
  toDay: DayKey;
  /** Epoch ms. */
  now: number;
  /** Ignore this appointment (used when moving it). */
  excludeAppointmentId?: Id;
}
export interface DaySlots { day: DayKey; slots: Slot[] }
export interface SlotsResult {
  days: DaySlots[];
  /** Set when the requested range has no slots: the next day (within the booking horizon) that has one. */
  nextAvailable?: { day: DayKey; slot: Slot };
}
export type StartRejection = 'outside_hours' | 'too_soon' | 'too_far' | 'time_off' | 'conflict';
export type BookFailReason = StartRejection | 'staff_unavailable' | 'service_inactive';

const BLOCKING: ReadonlySet<string> = new Set(['awaiting_deposit', 'pending', 'confirmed', 'completed', 'no_show']);

/** Does the appointment occupy its staff member's time at `nowMs`? */
export function isBlocking(a: Appointment, nowMs: number): boolean {
  if (!BLOCKING.has(a.status)) return false;
  if (a.status === 'awaiting_deposit' && a.holdExpiresAt !== undefined) return toMs(a.holdExpiresAt) > nowMs;
  return true;
}

/** Staff who may perform the service, honouring the customer's choice. */
export function eligibleStaff(service: Service, staff: Staff[], choice: Id | 'any'): Staff[] {
  return staff
    .filter((s) => s.active)
    .filter((s) => service.staffIds.length === 0 || service.staffIds.includes(s.id))
    .filter((s) => choice === 'any' || s.id === choice)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

interface StaffDayContext {
  q: SlotQuery;
  staff: Staff;
  busy: Appointment[];
  off: TimeOff[];
  maxDay: DayKey;
}

function context(q: SlotQuery, staff: Staff): StaffDayContext {
  const busy = q.appointments.filter(
    (a) => a.staffId === staff.id && a.id !== q.excludeAppointmentId && isBlocking(a, q.now),
  );
  const off = q.timeOff.filter((t) => t.staffId === undefined || t.staffId === staff.id);
  return { q, staff, busy, off, maxDay: addDaysToKey(dayKeyOf(q.now, q.tz), q.settings.maxAdvanceDays) };
}

function rangesFor(q: AvailabilityData, staffId: Id, day: DayKey) {
  const wd = weekdayOfDay(day);
  return q.weeklyHours.find((w) => w.staffId === staffId && w.weekday === wd)?.ranges ?? [];
}

const REJECTION_RANK: Record<StartRejection, number> = { outside_hours: 0, too_far: 1, too_soon: 2, time_off: 3, conflict: 4 };

/** Why `start` cannot be booked with this staff member, or null when it can. */
function rejectStart(c: StaffDayContext, startMs: number): StartRejection | null {
  const { q } = c;
  const day = dayKeyOf(startMs, q.tz);
  const duration = q.service.durationMin;
  const fits = rangesFor(q, c.staff.id, day).some((r) => {
    const rs = parseHHMM(r.start), re = parseHHMM(r.end);
    const from = atMinuteOfDay(day, rs, q.tz);
    const offset = (startMs - from) / MINUTE;
    return startMs >= from && offset % q.settings.slotStepMin === 0 && startMs + duration * MINUTE <= atMinuteOfDay(day, re, q.tz);
  });
  if (!fits) return 'outside_hours';
  if (day > c.maxDay) return 'too_far';
  if (startMs < q.now + q.settings.leadTimeMin * MINUTE) return 'too_soon';
  const endMs = startMs + duration * MINUTE;
  if (c.off.some((t) => startMs < toMs(t.end) && toMs(t.start) < endMs)) return 'time_off';
  if (overlapsBusy(c.busy, startMs, endMs + q.service.bufferMin * MINUTE)) return 'conflict';
  return null;
}

function overlapsBusy(busy: Appointment[], startMs: number, blockedUntilMs: number): boolean {
  return busy.some((a) => startMs < toMs(a.blockedUntil) && toMs(a.start) < blockedUntilMs);
}

function slotsForStaffDay(c: StaffDayContext, day: DayKey): Slot[] {
  const { q } = c;
  const out: Slot[] = [];
  for (const r of rangesFor(q, c.staff.id, day)) {
    const rs = parseHHMM(r.start), re = parseHHMM(r.end);
    for (let m = rs; m + q.service.durationMin <= re; m += q.settings.slotStepMin) {
      const startMs = atMinuteOfDay(day, m, q.tz);
      if (dayKeyOf(startMs, q.tz) !== day || rejectStart(c, startMs) !== null) continue;
      out.push({ start: toIso(startMs), end: toIso(startMs + q.service.durationMin * MINUTE), staffId: c.staff.id });
    }
  }
  return out;
}

/** Bookable slots per day; with staffChoice 'any' each start appears once, assigned to the first free staff member. */
export function getSlots(q: SlotQuery): SlotsResult {
  const contexts = eligibleStaff(q.service, q.staff, q.staffChoice).map((s) => context(q, s));
  const days: DaySlots[] = eachDay(q.fromDay, q.toDay).map((day) => {
    const byStart = new Map<IsoUtc, Slot>();
    for (const c of contexts) for (const s of slotsForStaffDay(c, day)) if (!byStart.has(s.start)) byStart.set(s.start, s);
    return { day, slots: [...byStart.values()].sort((a, b) => a.start.localeCompare(b.start)) };
  });
  const result: SlotsResult = { days };
  if (days.every((d) => d.slots.length === 0)) {
    const next = nextFreeSlots({ ...q, fromDay: addDaysToKey(q.toDay, 1) }, 1)[0];
    if (next) result.nextAvailable = { day: dayKeyOf(toMs(next.start), q.tz), slot: next };
  }
  return result;
}

/** The first `n` bookable slots from `fromDay` (inclusive) onward, within the booking horizon. */
export function nextFreeSlots(q: Omit<SlotQuery, 'toDay'>, n: number, afterMs = -Infinity): Slot[] {
  const contexts = eligibleStaff(q.service, q.staff, q.staffChoice).map((s) => context({ ...q, toDay: q.fromDay }, s));
  const horizon = addDaysToKey(dayKeyOf(q.now, q.tz), q.settings.maxAdvanceDays);
  const out: Slot[] = [];
  for (let day = q.fromDay; day <= horizon && out.length < n; day = addDaysToKey(day, 1)) {
    const seen = new Set<IsoUtc>();
    const slots: Slot[] = [];
    for (const c of contexts) {
      for (const s of slotsForStaffDay(c, day)) if (!seen.has(s.start) && toMs(s.start) > afterMs) { seen.add(s.start); slots.push(s); }
    }
    slots.sort((a, b) => a.start.localeCompare(b.start));
    out.push(...slots.slice(0, n - out.length));
  }
  return out;
}

export interface BookingRequest {
  serviceId: Id;
  staffId: Id | 'any';
  start: IsoUtc;
  source: AppointmentSource;
}
export type CanBook =
  | { ok: true; staffId: Id; start: IsoUtc; end: IsoUtc; blockedUntil: IsoUtc }
  | { ok: false; reason: BookFailReason; nextSlots: Slot[] };

/**
 * Can this request be booked right now? Link and waitlist bookings obey every rule. Owner bookings
 * may override working hours, lead time, horizon and time off, but can never double-book.
 * On `conflict` the result carries the next 3 free slots.
 */
export function canBook(q: Omit<SlotQuery, 'fromDay' | 'toDay' | 'staffChoice' | 'service'>, service: Service, req: BookingRequest): CanBook {
  const startMs = toMs(req.start);
  const full: SlotQuery = { ...q, service, staffChoice: req.staffId, fromDay: dayKeyOf(startMs, q.tz), toDay: dayKeyOf(startMs, q.tz) };
  if (!service.active) return { ok: false, reason: 'service_inactive', nextSlots: [] };
  const staff = eligibleStaff(service, q.staff, req.staffId);
  if (staff.length === 0) return { ok: false, reason: 'staff_unavailable', nextSlots: [] };
  const endMs = startMs + service.durationMin * MINUTE;
  const blockedUntil = endMs + service.bufferMin * MINUTE;
  let worst: StartRejection = 'outside_hours';
  for (const s of staff) {
    const c = context(full, s);
    const rejection = req.source === 'owner'
      ? (overlapsBusy(c.busy, startMs, blockedUntil) ? 'conflict' : null)
      : rejectStart(c, startMs);
    if (rejection === null) return { ok: true, staffId: s.id, start: req.start, end: toIso(endMs), blockedUntil: toIso(blockedUntil) };
    if (REJECTION_RANK[rejection] > REJECTION_RANK[worst]) worst = rejection;
  }
  const nextSlots = worst === 'conflict' ? nextFreeSlots(full, 3, startMs) : [];
  return { ok: false, reason: worst, nextSlots };
}

export interface Gap { staffId: Id; start: IsoUtc; end: IsoUtc; minutes: number }

/**
 * Free windows inside working hours on `day` that are at least `minMinutes` long, clipped to the
 * future. Powers the owner's tappable "פנוי · 45 דק׳" rows on Today.
 */
export function findGaps(
  data: AvailabilityData, day: DayKey, nowMs: number, minMinutes = 30,
): Gap[] {
  const gaps: Gap[] = [];
  for (const staff of data.staff.filter((s) => s.active).sort((a, b) => a.sortOrder - b.sortOrder)) {
    const busy = data.appointments
      .filter((a) => a.staffId === staff.id && isBlocking(a, nowMs))
      .map((a) => [toMs(a.start), toMs(a.blockedUntil)] as const);
    const off = data.timeOff.filter((t) => t.staffId === undefined || t.staffId === staff.id).map((t) => [toMs(t.start), toMs(t.end)] as const);
    for (const r of rangesFor(data, staff.id, day)) {
      const rangeStart = atMinuteOfDay(day, parseHHMM(r.start), data.tz);
      const rangeEnd = atMinuteOfDay(day, parseHHMM(r.end), data.tz);
      const blocks = [...busy, ...off].filter(([bs, be]) => be > rangeStart && bs < rangeEnd).sort((a, b) => a[0] - b[0]);
      let cursor = Math.max(rangeStart, ceilTo(nowMs, 5 * MINUTE));
      for (const [bs, be] of [...blocks, [rangeEnd, rangeEnd] as const]) {
        const gapEnd = Math.min(bs, rangeEnd);
        if (gapEnd - cursor >= minMinutes * MINUTE) pushGap(gaps, staff.id, cursor, gapEnd);
        cursor = Math.max(cursor, be);
      }
    }
  }
  return gaps.sort((a, b) => a.start.localeCompare(b.start));
}
function ceilTo(ms: number, unit: number): number { return Math.ceil(ms / unit) * unit; }
function pushGap(gaps: Gap[], staffId: Id, s: number, e: number): void {
  gaps.push({ staffId, start: toIso(s), end: toIso(e), minutes: Math.floor((e - s) / MINUTE) });
}
